import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Globe,
  Plug,
  Radar,
  Ship,
  Truck,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useRenderMode } from '@/hooks/useRenderMode';

type ApiState = 'json' | 'config' | 'none';
type DeliveryState = 'live' | 'learning' | 'blocked';

interface PortFunction {
  id: string;
  name: string;
  purpose: string;
  modules: string[];
  basePath: string;
  apiState: ApiState;
  state: DeliveryState;
  readiness: number;
  extensionReady: boolean;
  note: string;
}

interface PortnetSearchResult {
  pageUrl: string;
  pageTitle: string;
  rowCount: number;
  columns: string[];
  rows: Array<Record<string, string>>;
  csvText?: string;
  searchMode?: string;
  downloadMethod?: 'none' | 'generated' | 'native';
}

interface PortnetContainerJournalResult {
  pageUrl: string;
  pageTitle: string;
  containerNo: string;
  inputContainers: string[];
  rowCount: number;
  summary: Record<string, string>;
  overview: Record<string, string>;
  activities: Array<Record<string, string>>;
  results: Array<{
    containerNo: string;
    summary: Record<string, string>;
    overview: Record<string, string>;
    activities: Array<Record<string, string>>;
  }>;
}

interface ExtensionMessage {
  type?: string;
  requestId?: string;
  result?: unknown;
  error?: string;
}

interface ExtensionTab {
  id?: number;
  url?: string;
  active?: boolean;
}

interface ExtensionBridge {
  runtime?: {
    id?: string;
    onMessage?: {
      addListener: (listener: (message: ExtensionMessage) => void) => void;
      removeListener: (listener: (message: ExtensionMessage) => void) => void;
    };
  };
  tabs?: {
    query: (
      queryInfo: { active?: boolean; currentWindow?: boolean },
      callback: (tabs: ExtensionTab[]) => void,
    ) => void;
    sendMessage: (tabId: number, message: Record<string, unknown>) => Promise<unknown>;
  };
  scripting?: {
    executeScript: (options: { target: { tabId: number }; files: string[] }) => Promise<unknown>;
  };
}

const PORT_FUNCTIONS: PortFunction[] = [
  {
    id: 'vessel-schedule',
    name: 'Vessel Schedule',
    purpose: '按船名 / 航次或日期范围查询船期',
    modules: ['Freight Management'],
    basePath: '/report/bizfn/bass/summary/v1.0',
    apiState: 'json',
    state: 'live',
    readiness: 85,
    extensionReady: true,
    note: '已接 extension，可查 vessel+voyage、date range，可选生成 CSV。',
  },
  {
    id: 'container-journal',
    name: 'Container Journal',
    purpose: '查单柜或多柜轨迹，并把 JSON 写入数据库',
    modules: ['Cont Trucking', 'Freight Management'],
    basePath: '/report/bizfn/dcon/summary/v1.0',
    apiState: 'json',
    state: 'live',
    readiness: 95,
    extensionReady: true,
    note: '已接 extension，支持多柜换行查询，已通自动入库。',
  },
  {
    id: 'process-delivery',
    name: 'Process Delivery',
    purpose: '按 container / BL / auth / batch 查可处理 delivery',
    modules: ['Cont Trucking'],
    basePath: '/ngpn/edo/bizfn/process/searchEdoBySpecific/v1.0',
    apiState: 'json',
    state: 'learning',
    readiness: 35,
    extensionReady: false,
    note: '已确认 search API 与 payload，尚未做 extension action。',
  },
  {
    id: 'pending-export',
    name: 'Pending Export Container',
    purpose: '监控出口柜 pending、cut-off、VGM 等待处理项',
    modules: ['Cont Trucking', 'Freight Management'],
    basePath: '/ngpn/pec/bizfn/search/summary/v1.0',
    apiState: 'json',
    state: 'learning',
    readiness: 25,
    extensionReady: false,
    note: '已确认 summary API，当前仍停在第 1 步盘点。',
  },
  {
    id: 'pregate',
    name: 'Pregate',
    purpose: '按 Vehicle No. 做 pre-gate 处理与 gate flow 监控',
    modules: ['Cont Trucking'],
    basePath: '/ngpn/pg/bizfn/search/summary/v1.0',
    apiState: 'json',
    state: 'learning',
    readiness: 35,
    extensionReady: false,
    note: '已拿到 config 与大量 enquire/detail/process endpoint 常量。',
  },
  {
    id: 'smart-booking',
    name: 'Smart Booking',
    purpose: '预约 / slot booking 工作流',
    modules: ['Cont Trucking'],
    basePath: '未确认',
    apiState: 'none',
    state: 'blocked',
    readiness: 10,
    extensionReady: false,
    note: '当前账号下入口未展开，route / API 仍未暴露。',
  },
  {
    id: 'edo',
    name: 'Electronic Delivery Order',
    purpose: 'EDO 模块本体，覆盖 delivery order 查询与处理流程',
    modules: ['Cont Trucking'],
    basePath: '/ngpn/edo/bizfn/public/dpre/configurations/v1.0',
    apiState: 'config',
    state: 'learning',
    readiness: 35,
    extensionReady: false,
    note: '与 Process Delivery 同属 EDO 模块，当前先完成了配置与 search 探查。',
  },
  {
    id: 'esn',
    name: 'Electronic Shipping Note',
    purpose: 'ESN 创建 / 查询，含 VGM 与特种箱相关配置',
    modules: ['Cont Trucking', 'Freight Management'],
    basePath: '/esn/bizfn/getConfigItems/v1.0',
    apiState: 'config',
    state: 'learning',
    readiness: 30,
    extensionReady: false,
    note: '已确认独立 ESN 路由与 config API，主业务 search/create API 还没继续抓。',
  },
];

function StateBadge({ state }: { state: DeliveryState }) {
  if (state === 'live') {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Live</Badge>;
  }
  if (state === 'blocked') {
    return <Badge variant="destructive">Blocked</Badge>;
  }
  return <Badge variant="secondary">Step 1 Done</Badge>;
}

function ApiBadge({ apiState }: { apiState: ApiState }) {
  if (apiState === 'json') {
    return <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">JSON API</Badge>;
  }
  if (apiState === 'config') {
    return <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Config API</Badge>;
  }
  return <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">No API Yet</Badge>;
}

function isExtensionBridgeAvailable(): boolean {
  const browserWindow = globalThis as typeof globalThis & { chrome?: ExtensionBridge };
  const bridge = browserWindow.chrome;
  return Boolean(
    bridge?.runtime?.id
      && bridge.runtime.onMessage
      && typeof bridge.tabs?.query === 'function'
      && typeof bridge.scripting?.executeScript === 'function',
  );
}

const RPA_TARGET_PATTERNS = [
  /^https:\/\/([^.]+\.)*portnet\.com\//i,
  /^https:\/\/([^.]+\.)*portnet\.com\.sg\//i,
  /^https:\/\/([^.]+\.)*tradeweb\.sg\//i,
  /^https:\/\/cargo\.lch-logistics\.com\//i,
];

function isRpaTargetUrl(url?: string): boolean {
  return typeof url === 'string' && RPA_TARGET_PATTERNS.some((pattern) => pattern.test(url));
}

function queryTabs(bridge: ExtensionBridge, queryInfo: { active?: boolean; currentWindow?: boolean }): Promise<ExtensionTab[]> {
  return new Promise((resolve) => {
    bridge.tabs?.query(queryInfo, (tabs) => resolve(tabs ?? []));
  });
}

function isIgnorableSendMessageError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /message channel closed before a response was received/i.test(message)
    || /listener indicated an asynchronous response/i.test(message);
}

async function resolveRpaTargetTab(bridge: ExtensionBridge): Promise<ExtensionTab> {
  const tabs = await queryTabs(bridge, { currentWindow: true });
  const candidates = tabs
    .filter((tab) => tab.id && isRpaTargetUrl(tab.url))
    .sort((left, right) => Number(Boolean(right.active)) - Number(Boolean(left.active)));

  if (candidates.length === 0) {
    throw new Error('No PortNet tab found. 请先打开并停留在已登录的 PortNet 页面');
  }

  for (const tab of candidates) {
    if (!tab.id) continue;
    try {
      try {
        await bridge.tabs?.sendMessage(tab.id, { type: 'PING' });
      } catch {
        await bridge.scripting?.executeScript({
          target: { tabId: tab.id },
          files: ['content.js'],
        });
        await bridge.tabs?.sendMessage(tab.id, { type: 'PING' });
      }
      return tab;
    } catch {
      // Try next matching portal tab
    }
  }

  throw new Error('PortNet 页面未注入扩展脚本，且自动注入失败。请确认当前标签页是可访问的 PortNet 页面');
}

function sendRpaAction(action: string, payload: unknown, timeout = 90000): Promise<unknown> {
  if (!isExtensionBridgeAvailable()) {
    return Promise.reject(new Error('当前页面不在 Chrome Extension 环境里'));
  }

  const browserWindow = globalThis as typeof globalThis & { chrome?: ExtensionBridge };
  const bridge = browserWindow.chrome as ExtensionBridge;

  return new Promise((resolve, reject) => {
    const requestId = `rpa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;

    const cleanup = () => {
      bridge.runtime?.onMessage?.removeListener(listener);
      clearTimeout(timer);
    };

    const listener = (message: { type?: string; requestId?: string; result?: unknown; error?: string }) => {
      if (message.type !== 'RPA_RESULT' || message.requestId !== requestId || settled) return;
      settled = true;
      cleanup();
      if (message.error) reject(new Error(message.error));
      else resolve(message.result);
    };

    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('RPA timeout'));
    }, timeout);

    bridge.runtime?.onMessage?.addListener(listener);
    void resolveRpaTargetTab(bridge)
      .then((tab) => {
        if (!tab.id) {
          throw new Error('No PortNet tab found');
        }
        return bridge.tabs?.sendMessage(tab.id, { type: 'RPA_ACTION', action, payload, requestId })
          .catch((error) => {
            if (!isIgnorableSendMessageError(error)) {
              throw error;
            }
          });
      })
      .catch((error: unknown) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error instanceof Error ? error : new Error(String(error)));
      });
  });
}

function KeyValueGrid({ data }: { data: Record<string, string> }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="rounded-lg border border-border/70 bg-background px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{key}</div>
          <div className="mt-1 text-sm text-foreground">{value || '-'}</div>
        </div>
      ))}
    </div>
  );
}

export default function PortSystemDashboardPage() {
  const isMobile = useRenderMode() === 'mobile';
  const location = useLocation();
  const [vesselName, setVesselName] = useState('OOCL CHARLESTON');
  const [voyage, setVoyage] = useState('256E');
  const [vesselLoading, setVesselLoading] = useState(false);
  const [vesselError, setVesselError] = useState<string | null>(null);
  const [vesselResult, setVesselResult] = useState<PortnetSearchResult | null>(null);
  const [containerInput, setContainerInput] = useState('ONEU3320236');
  const [containerLoading, setContainerLoading] = useState(false);
  const [containerError, setContainerError] = useState<string | null>(null);
  const [containerResult, setContainerResult] = useState<PortnetContainerJournalResult | null>(null);

  const moduleContext = useMemo(() => {
    if (location.pathname.startsWith('/hms') || location.pathname.startsWith('/tms')) {
      return {
        title: 'Port System Dashboard',
        subtitle: 'Container Trucking · Integration',
        accent: 'text-blue-600',
        icon: Truck,
      };
    }
    return {
      title: 'Port System Dashboard',
      subtitle: 'Freight Management · Integration',
      accent: 'text-indigo-600',
      icon: Ship,
    };
  }, [location.pathname]);

  const vesselPreview = vesselResult?.rows.slice(0, 3) ?? [];
  const containerPreview = containerResult?.results.slice(0, 3) ?? [];

  async function runVesselSearch() {
    setVesselLoading(true);
    setVesselError(null);
    try {
      const result = await sendRpaAction('portnet.searchVesselSchedule', {
        vesselName: vesselName.trim(),
        voyage: voyage.trim() || undefined,
      });
      setVesselResult(result as PortnetSearchResult);
    } catch (error) {
      setVesselError(error instanceof Error ? error.message : String(error));
    } finally {
      setVesselLoading(false);
    }
  }

  async function runContainerSearch() {
    setContainerLoading(true);
    setContainerError(null);
    try {
      const result = await sendRpaAction('portnet.searchContainerJournal', {
        containerNo: containerInput,
      });
      setContainerResult(result as PortnetContainerJournalResult);
    } catch (error) {
      setContainerError(error instanceof Error ? error.message : String(error));
    } finally {
      setContainerLoading(false);
    }
  }

  const stats = useMemo(() => ({
    total: PORT_FUNCTIONS.length,
    live: PORT_FUNCTIONS.filter((item) => item.state === 'live').length,
    withJsonApi: PORT_FUNCTIONS.filter((item) => item.apiState === 'json').length,
    extensionReady: PORT_FUNCTIONS.filter((item) => item.extensionReady).length,
  }), []);

  const ContextIcon = moduleContext.icon;

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden border-border/70">
        <CardContent className={isMobile ? 'space-y-3 p-4' : 'space-y-4 p-5'}>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10">
                  <Plug className={`h-5 w-5 ${moduleContext.accent}`} />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {moduleContext.subtitle}
                  </div>
                  <div className="text-xl font-semibold text-foreground">{moduleContext.title}</div>
                </div>
              </div>
              <p className="max-w-3xl text-sm text-muted-foreground">
                这里统一整理 PortNet 相关的 8 个功能，给 Chrome Extension 做本地触发入口与 readiness 盘点。
                当前真正可直接在 extension 内跑的，是 `Vessel Schedule` 和 `Container Journal`。
              </p>
            </div>
            <div className="hidden shrink-0 rounded-2xl border border-border/70 bg-muted/30 px-4 py-3 md:block">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <ContextIcon className={`h-4 w-4 ${moduleContext.accent}`} />
                Module Context
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{moduleContext.subtitle}</div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="border-emerald-200 bg-emerald-50/70">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Live Skills
                </div>
                <div className="mt-2 text-3xl font-semibold text-emerald-900">{stats.live}</div>
                <div className="mt-1 text-xs text-emerald-800/80">已经可在 extension 内触发</div>
              </CardContent>
            </Card>

            <Card className="border-sky-200 bg-sky-50/70">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-sky-700">
                  <Globe className="h-3.5 w-3.5" />
                  API Confirmed
                </div>
                <div className="mt-2 text-3xl font-semibold text-sky-900">{stats.withJsonApi}</div>
                <div className="mt-1 text-xs text-sky-800/80">已确认主业务 JSON API 的功能数</div>
              </CardContent>
            </Card>

            <Card className="border-violet-200 bg-violet-50/70">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-violet-700">
                  <Radar className="h-3.5 w-3.5" />
                  Extension Ready
                </div>
                <div className="mt-2 text-3xl font-semibold text-violet-900">{stats.extensionReady}</div>
                <div className="mt-1 text-xs text-violet-800/80">已接 Chrome Extension 触发链路</div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/70">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-amber-700">
                  <CircleDashed className="h-3.5 w-3.5" />
                  Scope
                </div>
                <div className="mt-2 text-3xl font-semibold text-amber-900">{stats.total}</div>
                <div className="mt-1 text-xs text-amber-800/80">规划中的 Port System 功能总数</div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/70">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Current Trigger Surface</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-border/70 bg-muted/20 p-3">
            <div className="text-sm font-medium text-foreground">Primary Entry</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Chrome Extension 内的模块菜单，建议放在 `Cont Trucking` 与 `Freight Management` 下的 `Integration → Port System`。
            </div>
          </div>
          <div className="rounded-xl border border-border/70 bg-muted/20 p-3">
            <div className="text-sm font-medium text-foreground">Current Local Test</div>
            <div className="mt-1 text-xs text-muted-foreground">
              直接用下面的 `Quick Test`。前提是当前活动 tab 必须就是已登录的 PortNet 页面。
            </div>
          </div>
          <div className="rounded-xl border border-border/70 bg-muted/20 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" />
              <div className="text-xs text-muted-foreground">
                `Smart Booking` 仍是 blocker。当前账号下菜单未展开，无法确认可用 route 或 API。
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 xl:grid-cols-2">
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Quick Test · Vessel Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Input value={vesselName} onChange={(event) => setVesselName(event.target.value)} placeholder="Vessel Name" />
              <Input value={voyage} onChange={(event) => setVoyage(event.target.value)} placeholder="Voyage" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => void runVesselSearch()} disabled={vesselLoading}>
                {vesselLoading ? '查询中...' : '查询 Vessel Schedule'}
              </Button>
              <div className="text-xs text-muted-foreground">
                当前默认值是 `OOCL CHARLESTON / 256E`
              </div>
            </div>
            {vesselError ? <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{vesselError}</div> : null}
            {vesselResult ? (
              <div className="space-y-3 rounded-xl border border-border/70 bg-background p-3">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>命中: {vesselResult.rowCount}</span>
                  <span>模式: {vesselResult.searchMode ?? '-'}</span>
                  <span>页面: {vesselResult.pageTitle}</span>
                </div>
                {vesselPreview.map((row, index) => (
                  <div key={`${row.Vessel ?? 'row'}-${index}`} className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Result {index + 1}</div>
                    <KeyValueGrid data={row} />
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="border-sky-200 bg-sky-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Quick Test · Container Journal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={containerInput}
              onChange={(event) => setContainerInput(event.target.value)}
              placeholder="每行一个 container no"
              className="min-h-28"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => void runContainerSearch()} disabled={containerLoading}>
                {containerLoading ? '查询中...' : '查询 Container Journal'}
              </Button>
              <div className="text-xs text-muted-foreground">
                支持单柜或多柜换行；查询成功后会自动入库
              </div>
            </div>
            {containerError ? <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{containerError}</div> : null}
            {containerResult ? (
              <div className="space-y-3 rounded-xl border border-border/70 bg-background p-3">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>输入: {containerResult.inputContainers.length}</span>
                  <span>命中: {containerResult.rowCount}</span>
                  <span>首柜: {containerResult.containerNo}</span>
                </div>
                {containerPreview.map((item) => (
                  <div key={item.containerNo} className="rounded-lg border border-border/70 bg-muted/20 p-3">
                    <div className="mb-2 text-sm font-medium text-foreground">{item.containerNo}</div>
                    <KeyValueGrid data={item.summary} />
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3 xl:grid-cols-2">
        {PORT_FUNCTIONS.map((item) => (
          <Card key={item.id} className="border-border/70">
            <CardHeader className="space-y-3 pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base font-semibold">{item.name}</CardTitle>
                  <div className="mt-1 text-sm text-muted-foreground">{item.purpose}</div>
                </div>
                <StateBadge state={item.state} />
              </div>
              <div className="flex flex-wrap gap-2">
                <ApiBadge apiState={item.apiState} />
                <Badge variant="outline" className={item.extensionReady ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : ''}>
                  {item.extensionReady ? 'Extension Ready' : 'Extension Pending'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <div className="grid gap-2 text-sm sm:grid-cols-2">
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Modules</div>
                  <div className="mt-1 text-foreground">{item.modules.join(' / ')}</div>
                </div>
                <div className="rounded-lg bg-muted/30 px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Readiness</div>
                  <div className="mt-1 text-foreground">{item.readiness}%</div>
                </div>
              </div>

              <div className="rounded-lg border border-border/70 bg-background px-3 py-2">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Base Path</div>
                <div className="mt-1 break-all font-mono text-xs text-foreground">{item.basePath}</div>
              </div>

              <div className="rounded-lg border border-border/70 bg-background px-3 py-2 text-sm text-muted-foreground">
                {item.note}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
